// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import xGoogleAnalyticsUpdate from "@sc/App/GoogleAnalyticsConnector/Handlers/GA4";
import { setTriggerDefaultShippingInfo } from "@sc/App/GoogleAnalyticsConnector/Store/Reducers";
import { getGoogleAnalyticsConnectorMapState } from "@sc/App/GoogleAnalyticsConnector/Store/Selectors/GoogleAnalyticsSelectors";
import { getDataLayer } from "@sc/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import { XNGAGE_CUSTOM_WIDGET } from "@sc/XDL/constants";
import { parseProductCategories } from "@sc/XDL/Utilities";
import React, { FC, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { css } from "styled-components";

const enum fields {
    // Basic Events
    trackGoogleAnalyticsEvents = "trackGoogleAnalyticsEvents",
    checkoutFlowEvents = "checkoutFlowEvents",
    addToCartEvent = "addToCartEvent",
    addToCartEventName = "addToCartEventName",
    purchaseEvent = "purchaseEvent",
    purchaseEventName = "purchaseEventName",
    removeFromCartLineEvent = "removeFromCartLineEvent",
    removeFromCartLineEventName = "removeFromCartLineEventName",
    pdpViewEvent = "pdpViewEvent",
    pdpViewEventName = "pdpViewEventName",
    addToWishListEvent = "addToWishListEvent",
    addToWishListEventName = "addToWishListEventName",
    productImpressionEvent = "productImpressionEvent",
    productImpressionEventName = "productImpressionEventName",
    selectItemEvent = "selectItemEvent",
    selectItemEventName = "selectItemEventName",
    viewCartEvent = "viewCartEvent",
    viewCartEventName = "viewCartEventName",
    // Custom Events
    loginEvent = "loginEvent",
    loginEventName = "loginEventName",
    loginIssueEvent = "loginIssueEvent",
    loginIssueEventName = "loginIssueEventName",
    searchEvent = "searchEvent",
    searchEventName = "searchEventName",
    contactUsEvent = "contactUsEvent",
    contactUsEventName = "contactUsEventName",
    plpFilterEvent = "plpFilterEvent",
    plpFilterEventName = "plpFilterEventName",
    plpSearchEvent = "plpSearchEvent",
    plpSearchEventName = "plpSearchEventName",
    pdpShareEvent = "pdpShareEvent",
    pdpShareEventName = "pdpShareEventName",
    requestAQuoteEvent = "requestAQuoteEvent",
    requestAQuoteEventName = "requestAQuoteEventName",
    myAcctQuickLinksEvent = "myAcctQuickLinksEvent",
    myAcctQuickLinksEventName = "myAcctQuickLinksEventName",
    myAcctFlyoutEvent = "myAcctFlyoutEvent",
    myAcctFlyoutEventName = "myAcctFlyoutEventName",
    navBarClickEvent = "navBarClickEvent",
    navBarClickEventName = "navBarClickEventName",
    toolBarNavEvent = "toolBarNavEvent",
    toolBarNavEventName = "toolBarNavEventName",
    homepageRibbonevent = "homepageRibbonevent",
    homepageRibbonEventName = "homepageRibbonEventName",
    requestAccountEvent = "requestAccountEvent",
    requestAccountEventName = "requestAccountEventName",
    locationsEvent = "locationsEvent",
    locationsEventName = "locationsEventName",
    myacctMylistsEvent = "myacctMylistsEvent",
    myacctMylistsEventName = "myacctMylistsEventName",
    myacctQuotesEvent = "myacctQuotesEvent",
    myacctQuotesEventName = "myacctQuotesEventName",
    addListToCartEvent = "addListToCartEvent",
    addListToCartEventName = "addListToCartEventName",
    myacctListsMoreactionsEvent = "myacctListsMoreactionsEvent",
    myacctListsMoreactionsEventName = "myacctListsMoreactionsEventName",
    quickOrderSearchEvent = "quickOrderSearchEvent",
    quickOrderSearchEventName = "quickOrderSearchEventName",
    quickOrderUploadEvent = "quickOrderUploadEvent",
    quickOrderUploadEventName = "quickOrderUploadEventName",
    requestProductEvent = "requestProductEvent",
    requestProductEventName = "requestProductEventName",
    editCartEvent = "editCartEvent",
    editCartEventName = "editCartEventName",
    reorderEvent = "reorderEvent",
    reorderEventName = "reorderEventName",
    myacctOrderhistoryDaterangeEvent = "myacctOrderhistoryDaterangeEvent",
    myacctOrderhistoryDaterangeEventName = "myacctOrderhistoryDaterangeEventName",
    myacctOrderhistoryShiptoEvent = "myacctOrderhistoryShiptoEvent",
    myacctOrderhistoryShiptoEventName = "myacctOrderhistoryShiptoEventName",
    myacctOrderEvent = "myacctOrderEvent",
    myacctOrderEventName = "myacctOrderEventName",
    quoteAcceptedEvent = "quoteAcceptedEvent",
    quoteAcceptedEventName = "quoteAcceptedEventName",
    quoteDeclinedEvent = "quoteDeclinedEvent",
    quoteDeclinedEventName = "quoteDeclinedEventName",
    quoteProposedEvent = "quoteProposedEvent",
    quoteProposedEventName = "quoteProposedEventName",
    contactSalesEvent = "contactSalesEvent",
    contactSalesEventName = "contactSalesEventName",
    pdpImageClickEvent = "pdpImageClickEvent",
    pdpImageClickEventName = "pdpImageClickEventName",
    myacctAddUserEvent = "myacctAddUserEvent",
    myacctAddUserEventName = "myacctAddUserEventName",
    myacctEditUserEvent = "myacctEditUserEvent",
    myacctEditUserEventName = "myacctEditUserEventName",
    myacctViewUserEvent = "myacctViewUserEvent",
    myacctViewUserEventName = "myacctViewUserEventName",
    footerClicksEvent = "footerClicksEvent",
    footerClicksEventName = "footerClicksEventName",
    fileDownloadEvent = "fileDownloadEvent",
    fileDownloadEventName = "fileDownloadEventName",
    pdpWarehouseInventoryEvent = "pdpWarehouseInventoryEvent",
    pdpWarehouseInventoryEventName = "pdpWarehouseInventoryEventName",
    industriesServedEvent = "industriesServedEvent",
    industriesServedEventName = "industriesServedEventName",
    myacctRecentOrdersEvent = "myacctRecentOrdersEvent",
    myacctRecentOrdersEventName = "myacctRecentOrdersEventName",
    // Settings fields
    includeProductBrand = "includeProductBrand",
    includeProductCategory = "includeProductCategory",
}

interface OwnProps extends WidgetProps {
    fields: {
        // Basic Events
        [fields.trackGoogleAnalyticsEvents]: boolean;
        [fields.checkoutFlowEvents]: boolean;
        [fields.addToCartEvent]: boolean;
        [fields.addToCartEventName]: string;
        [fields.purchaseEvent]: boolean;
        [fields.purchaseEventName]: string;
        [fields.removeFromCartLineEvent]: boolean;
        [fields.removeFromCartLineEventName]: string;
        [fields.pdpViewEvent]: boolean;
        [fields.pdpViewEventName]: string;
        [fields.addToWishListEvent]: boolean;
        [fields.addToWishListEventName]: string;
        [fields.productImpressionEvent]: boolean;
        [fields.productImpressionEventName]: string;
        [fields.selectItemEvent]: boolean;
        [fields.selectItemEventName]: string;
        [fields.viewCartEvent]: boolean;
        [fields.viewCartEventName]: string;
        // Custom Events
        [fields.loginEvent]: boolean;
        [fields.loginEventName]: string;
        [fields.loginIssueEvent]: boolean;
        [fields.loginIssueEventName]: string;
        [fields.searchEvent]: boolean;
        [fields.searchEventName]: string;
        [fields.contactUsEvent]: boolean;
        [fields.contactUsEventName]: string;
        [fields.plpFilterEvent]: boolean;
        [fields.plpFilterEventName]: string;
        [fields.plpSearchEvent]: boolean;
        [fields.plpSearchEventName]: string;
        [fields.pdpShareEvent]: boolean;
        [fields.pdpShareEventName]: string;
        [fields.requestAQuoteEvent]: boolean;
        [fields.requestAQuoteEventName]: string;
        [fields.myAcctQuickLinksEvent]: boolean;
        [fields.myAcctQuickLinksEventName]: string;
        [fields.myAcctFlyoutEvent]: boolean;
        [fields.myAcctFlyoutEventName]: string;
        [fields.navBarClickEvent]: boolean;
        [fields.navBarClickEventName]: string;
        [fields.toolBarNavEvent]: boolean;
        [fields.toolBarNavEventName]: string;
        [fields.homepageRibbonevent]: boolean;
        [fields.homepageRibbonEventName]: string;
        [fields.requestAccountEvent]: boolean;
        [fields.requestAccountEventName]: string;
        [fields.locationsEvent]: boolean;
        [fields.locationsEventName]: string;
        [fields.myacctMylistsEvent]: boolean;
        [fields.myacctMylistsEventName]: string;
        [fields.myacctQuotesEvent]: boolean;
        [fields.myacctQuotesEventName]: string;
        [fields.addListToCartEvent]: boolean;
        [fields.addListToCartEventName]: string;
        [fields.myacctListsMoreactionsEvent]: boolean;
        [fields.myacctListsMoreactionsEventName]: string;
        [fields.quickOrderSearchEvent]: boolean;
        [fields.quickOrderSearchEventName]: string;
        [fields.quickOrderUploadEvent]: boolean;
        [fields.quickOrderUploadEventName]: string;
        [fields.requestProductEvent]: boolean;
        [fields.requestProductEventName]: string;
        [fields.editCartEvent]: boolean;
        [fields.editCartEventName]: string;
        [fields.reorderEvent]: boolean;
        [fields.reorderEventName]: string;
        [fields.myacctOrderhistoryDaterangeEvent]: boolean;
        [fields.myacctOrderhistoryDaterangeEventName]: string;
        [fields.myacctOrderhistoryShiptoEvent]: boolean;
        [fields.myacctOrderhistoryShiptoEventName]: string;
        [fields.myacctOrderEvent]: boolean;
        [fields.myacctOrderEventName]: string;
        [fields.quoteAcceptedEvent]: boolean;
        [fields.quoteAcceptedEventName]: string;
        [fields.quoteDeclinedEvent]: boolean;
        [fields.quoteDeclinedEventName]: string;
        [fields.quoteProposedEvent]: boolean;
        [fields.quoteProposedEventName]: string;
        [fields.contactSalesEvent]: boolean;
        [fields.contactSalesEventName]: string;
        [fields.pdpImageClickEvent]: boolean;
        [fields.pdpImageClickEventName]: string;
        [fields.myacctAddUserEvent]: boolean;
        [fields.myacctAddUserEventName]: string;
        [fields.myacctEditUserEvent]: boolean;
        [fields.myacctEditUserEventName]: string;
        [fields.myacctViewUserEvent]: boolean;
        [fields.myacctViewUserEventName]: string;
        [fields.footerClicksEvent]: boolean;
        [fields.footerClicksEventName]: string;
        [fields.fileDownloadEvent]: boolean;
        [fields.fileDownloadEventName]: string;
        [fields.pdpWarehouseInventoryEvent]: boolean;
        [fields.pdpWarehouseInventoryEventName]: string;
        [fields.industriesServedEvent]: boolean;
        [fields.industriesServedEventName]: string;
        [fields.myacctRecentOrdersEvent]: boolean;
        [fields.myacctRecentOrdersEventName]: string;
        // Settings Fields
        [fields.includeProductBrand]: boolean;
        [fields.includeProductCategory]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        ...getGoogleAnalyticsConnectorMapState(state),
    };
};

const mapDispatchToProps = {
    xGoogleAnalyticsUpdate,
    resetShouldTrackPayment: () => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xGoogleAnalytics/setShouldTrackPayment",
            shouldTrackPayment: false,
        } as any);
    },
    resetShouldTrackBeginCheckout: () => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xGoogleAnalytics/setShouldTrackBeginOnePageCheckout",
            shouldTrackBeginOnePageCheckout: false,
        } as any);
    },
    setAfilliation: (affiliation?: string) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xGoogleAnalytics/setAffiliation",
            affiliation,
        } as any);
    },
    setCustomerNumbers: (billToNum?: string, shipToNum?: string) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xGoogleAnalytics/setCustomerNumbers",
            billToNum,
            shipToNum,
        } as any);
    },
    setSalesRep: (salesRep?: string) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xGoogleAnalytics/setSalesRep",
            salesRep,
        } as any);
    },
    setIsRep: (isRep?: string) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xGoogleAnalytics/setIsRep",
            isRep,
        } as any);
    },
    setCustomerType: (customerType?: string) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xGoogleAnalytics/setCustomerType",
            customerType,
        } as any);
    },
    setLoginType: (loginType?: string) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xGoogleAnalytics/setLoginType",
            loginType,
        } as any);
    },
    setWarehouse: (warehouse?: string) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xGoogleAnalytics/setWarehouse",
            warehouse,
        } as any);
    },
    resetShouldTrackShippingInfo: () => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xGoogleAnalytics/setShippingInfoEventTrackingFlags",
            shouldTrackShippingInfo: false,
        } as any);
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface GoogleAnalyticsConnectorStyles {
    container?: InjectableCss;
}

const googleAnalyticsConnectorStyles: GoogleAnalyticsConnectorStyles = {
    container: {
        css: css`
            background: #e5e5e5;
            padding: 10px;
        `,
    },
};

const styles = googleAnalyticsConnectorStyles;
const GoogleAnalyticsConnector: FC<Props> = ({
    xGoogleAnalyticsUpdate,
    fields,
    cart,
    isCartPage,
    xGoogleAnalytics,
    currentPage,
    currencyCode,
    affiliation,
    locationId,
    isPLP,
    isPDP,
    PLPProducts,
    PDPProduct,
    resetShouldTrackPayment,
    resetShouldTrackBeginCheckout,
    resetShouldTrackShippingInfo,
    userEmail,
    query,
    setAfilliation,
    billToNum,
    shipToNum,
    setCustomerNumbers,
    salesRep,
    setSalesRep,
    isRep,
    setIsRep,
    customerType,
    setCustomerType,
    loginType,
    setLoginType,
    warehouse,
    setWarehouse,
}) => {
    const { isInShell } = useShellContext();

    const lastViewedPDP = useRef("");
    const [PLPInfoLoaded, setPLPInfoLoaded] = useState<boolean>(false);

    // Placeholder in CMS view
    if (isInShell) {
        return (
            <StyledWrapper {...styles.container} data-test-selectore="GoogleAnalyticsConnector">
                <Typography>{translate("Google Analytics Connector Widget")}</Typography>
            </StyledWrapper>
        );
    }

    useEffect(() => {
        if (!fields.trackGoogleAnalyticsEvents) {
            return;
        }

        xGoogleAnalyticsUpdate({
            trackGoogleAnalytics: true,
            checkoutFlowEvents: fields["checkoutFlowEvents"] || false,
            addToCartEvent: fields["addToCartEvent"] || false,
            addToCartEventName: fields["addToCartEventName"] || "add_to_cart",
            purchaseEvent: fields["purchaseEvent"] || false,
            purchaseEventName: fields["purchaseEventName"] || "purchase",
            removeFromCartLineEvent: fields["removeFromCartLineEvent"] || false,
            removeFromCartLineEventName: fields["removeFromCartLineEventName"] || "remove_from_cart",
            addToWishListEvent: fields["addToWishListEvent"] || false,
            addToWishListEventName: fields["addToWishListEventName"] || "add_to_wishlist",
            pdpViewEvent: fields["pdpViewEvent"] || false,
            pdpViewEventName: fields["pdpViewEventName"] || "view_item",
            productImpressionEvent: fields["productImpressionEvent"] || false,
            productImpressionEventName: fields["productImpressionEventName"] || "view_item_list",
            selectItemEvent: fields["selectItemEvent"] || false,
            selectItemEventName: fields["selectItemEventName"] || "select_item",
            viewCartEvent: fields["viewCartEvent"] || false,
            viewCartEventName: fields["viewCartEventName"] || "view_cart",
            includeProductBrand: fields["includeProductBrand"] || false,
            includeProductCategory: fields["includeProductCategory"] || false,
            loginEvent: fields["loginEvent"] || false,
            loginEventName: fields["loginEventName"] || "login",
            loginIssueEvent: fields["loginIssueEvent"] || false,
            loginIssueEventName: fields["loginIssueEventName"] || "login_issue",
            searchEvent: fields["searchEvent"] || false,
            searchEventName: fields["searchEventName"] || "search",
            contactUsEvent: fields["contactUsEvent"] || false,
            contactUsEventName: fields["contactUsEventName"] || "contact_us",
            plpFilterEvent: fields["plpFilterEvent"] || false,
            plpFilterEventName: fields["plpFilterEventName"] || "plp_filter",
            plpSearchEvent: fields["plpSearchEvent"] || false,
            plpSearchEventName: fields["plpSearchEventName"] || "plp_search",
            pdpShareEvent: fields["pdpShareEvent"] || false,
            pdpShareEventName: fields["pdpShareEventName"] || "pdp_share",
            requestAQuoteEvent: fields["requestAQuoteEvent"] || false,
            requestAQuoteEventName: fields["requestAQuoteEventName"] || "request_a_quote",
            myAcctQuickLinksEvent: fields["myAcctQuickLinksEvent"] || false,
            myAcctQuickLinksEventName: fields["myAcctQuickLinksEventName"] || "my_acct_quicklinks",
            myAcctFlyoutEvent: fields["myAcctFlyoutEvent"] || false,
            myAcctFlyoutEventName: fields["myAcctFlyoutEventName"] || "my_acct_flyout",
            navBarClickEvent: fields["navBarClickEvent"] || false,
            navBarClickEventName: fields["navBarClickEventName"] || "nav_bar_click",
            toolBarNavEvent: fields["toolBarNavEvent"] || false,
            toolBarNavEventName: fields["toolBarNavEventName"] || "tool_bar_nav",
            homepageRibbonevent: fields["homepageRibbonevent"] || false,
            homepageRibbonEventName: fields["homepageRibbonEventName"] || "homepage_ribbon",
            requestAccountEvent: fields["requestAccountEvent"] || false,
            requestAccountEventName: fields["requestAccountEventName"] || "request_account",
            locationsEvent: fields["locationsEvent"] || false,
            locationsEventName: fields["locationsEventName"] || "locations",
            myacctMylistsEvent: fields["myacctMylistsEvent"] || false,
            myacctMylistsEventName: fields["myacctMylistsEventName"] || "myacct_mylists",
            myacctQuotesEvent: fields["myacctQuotesEvent"] || false,
            myacctQuotesEventName: fields["myacctQuotesEventName"] || "myacct_quotes",
            addListToCartEvent: fields["addListToCartEvent"] || false,
            addListToCartEventName: fields["addListToCartEventName"] || "add_list_to_cart",
            myacctListsMoreactionsEvent: fields["myacctListsMoreactionsEvent"] || false,
            myacctListsMoreactionsEventName: fields["myacctListsMoreactionsEventName"] || "myacct_lists_moreactions",
            quickOrderSearchEvent: fields["quickOrderSearchEvent"] || false,
            quickOrderSearchEventName: fields["quickOrderSearchEventName"] || "quick_order_search",
            quickOrderUploadEvent: fields["quickOrderUploadEvent"] || false,
            quickOrderUploadEventName: fields["quickOrderUploadEventName"] || "quick_order_upload",
            requestProductEvent: fields["requestProductEvent"] || false,
            requestProductEventName: fields["requestProductEventName"] || "request_product",
            editCartEvent: fields["editCartEvent"] || false,
            editCartEventName: fields["editCartEventName"] || "edit_cart",
            reorderEvent: fields["reorderEvent"] || false,
            reorderEventName: fields["reorderEventName"] || "reorder",
            myacctOrderhistoryDaterangeEvent: fields["myacctOrderhistoryDaterangeEvent"] || false,
            myacctOrderhistoryDaterangeEventName:
                fields["myacctOrderhistoryDaterangeEventName"] || "myacct_orderhistory_daterange",
            myacctOrderhistoryShiptoEvent: fields["myacctOrderhistoryShiptoEvent"] || false,
            myacctOrderhistoryShiptoEventName:
                fields["myacctOrderhistoryShiptoEventName"] || "myacct_orderhistory_shipto",
            myacctOrderEvent: fields["myacctOrderEvent"] || false,
            myacctOrderEventName: fields["myacctOrderEventName"] || "myacct_order",
            quoteAcceptedEvent: fields["quoteAcceptedEvent"] || false,
            quoteAcceptedEventName: fields["quoteAcceptedEventName"] || "quote_accepted",
            quoteDeclinedEvent: fields["quoteDeclinedEvent"] || false,
            quoteDeclinedEventName: fields["quoteDeclinedEventName"] || "quote_declined",
            quoteProposedEvent: fields["quoteProposedEvent"] || false,
            quoteProposedEventName: fields["quoteProposedEventName"] || "quote_proposed",
            contactSalesEvent: fields["contactSalesEvent"] || false,
            contactSalesEventName: fields["contactSalesEventName"] || "contact_sales",
            pdpImageClickEvent: fields["pdpImageClickEvent"] || false,
            pdpImageClickEventName: fields["pdpImageClickEventName"] || "pdp_image_click",
            myacctAddUserEvent: fields["myacctAddUserEvent"] || false,
            myacctAddUserEventName: fields["myacctAddUserEventName"] || "myacct_add_user",
            myacctEditUserEvent: fields["myacctEditUserEvent"] || false,
            myacctEditUserEventName: fields["myacctEditUserEventName"] || "myacct_edit_user",
            myacctViewUserEvent: fields["myacctViewUserEvent"] || false,
            myacctViewUserEventName: fields["myacctViewUserEventName"] || "myacct_view_user",
            footerClicksEvent: fields["footerClicksEvent"] || false,
            footerClicksEventName: fields["footerClicksEventName"] || "footer_clicks",
            fileDownloadEvent: fields["fileDownloadEvent"] || false,
            fileDownloadEventName: fields["fileDownloadEventName"] || "file_download",
            pdpWarehouseInventoryEvent: fields["pdpWarehouseInventoryEvent"] || false,
            pdpWarehouseInventoryEventName: fields["pdpWarehouseInventoryEventName"] || "pdp_warehouse_inventory",
            industriesServedEvent: fields["industriesServedEvent"] || false,
            industriesServedEventName: fields["industriesServedEventName"] || "industries_served",
            myacctRecentOrdersEvent: fields["myacctRecentOrdersEvent"] || false,
            myacctRecentOrdersEventName: fields["myacctRecentOrdersEventName"] || "myacct_recent_orders",
        });

        if (!sessionStorage.getItem("triggerDefaultShippingInfo") && fields["checkoutFlowEvents"]) {
            setTriggerDefaultShippingInfo(fields["checkoutFlowEvents"]);
        }
    }, [fields]);

    useEffect(() => {
        const dataLayer = getDataLayer();

        if (!cart) {
            return;
        }

        if (isCartPage && xGoogleAnalytics?.viewCartEvent) {
            setPLPInfoLoaded(false);

            const googleAnalyticsData = {
                event: xGoogleAnalytics?.viewCartEventName,
                ecommerce: {
                    currency: currencyCode,
                    value: cart.orderSubTotal,
                    affiliation,
                    bill_to_num: billToNum,
                    ship_to_num: shipToNum,
                    is_rep: isRep,
                    cust_type: customerType,
                    login_type: loginType,
                    warehouse,
                    sales_rep: salesRep,
                    items: cart?.cartLines?.map((cartItem, idx) => ({
                        item_id: cartItem.erpNumber,
                        item_name: cartItem.productName,
                        affiliation,
                        discount: cartItem.pricing
                            ? cartItem.pricing.unitNetPrice < cartItem.pricing.unitListPrice
                                ? cartItem.pricing.unitListPrice - cartItem.pricing.unitNetPrice
                                : 0
                            : 0,
                        price: cartItem.quoteRequired ? "Requires Quote" : cartItem.pricing?.unitNetPrice,
                        quantity: cartItem.qtyOrdered,
                        index: idx,
                        item_brand: xGoogleAnalytics["includeProductBrand"] ? cartItem.brand?.name || "" : undefined,
                        items_available: (cartItem.availability?.messageType !== 2 ? cartItem.qtyOnHand : 0) || "",
                        item_variant: "",
                        location_id: locationId,
                        item_list_id: "",
                        item_list_name: "Cart",
                        ...parseProductCategories(
                            cartItem?.properties?.itemCategoriesShortDescription,
                            true,
                            xGoogleAnalytics["includeProductCategory"],
                        ),
                    })),
                },
            };

            dataLayer.push({ ecommerce: null });
            dataLayer.push(googleAnalyticsData);
        }
    }, [isCartPage, cart?.id, xGoogleAnalytics?.viewCartEvent]);

    // disable default shipping info event trigger if we have multiple carriers
    useEffect(() => {
        if (cart?.carriers && cart?.carriers?.length > 1) {
            setTriggerDefaultShippingInfo(false);
        }
    }, [cart?.carriers]);

    // PLP Product Impression Event
    useEffect(() => {
        const dataLayer = getDataLayer();

        if (dataLayer && isPLP && PLPProducts?.length && !PLPInfoLoaded && xGoogleAnalytics?.productImpressionEvent) {
            setPLPInfoLoaded(true);

            const googleAnalyticsData = {
                event: xGoogleAnalytics?.productImpressionEventName,
                ecommerce: {
                    item_list_name: "PLP",
                    item_list_id: "",
                    url: window.location.href,
                    affiliation,
                    bill_to_num: billToNum,
                    ship_to_num: shipToNum,
                    is_rep: isRep,
                    cust_type: customerType,
                    login_type: loginType,
                    warehouse,
                    sales_rep: salesRep,
                    items: PLPProducts.map((product, idx) => ({
                        item_name: product?.productTitle,
                        item_id: product?.productNumber,
                        price: product?.pricing?.unitNetPrice,
                        items_available: product.inventory?.qtyOnHand || 0,
                        item_variant: "",
                        default_state: "No UOM selected",
                        index: idx,
                        item_brand: xGoogleAnalytics["includeProductBrand"] ? product?.brand?.name || "" : undefined,
                        item_list_name: "PLP",
                        item_list_id: "",
                        location_id: locationId,
                        ...parseProductCategories(
                            product?.properties?.itemCategoriesShortDescription,
                            true,
                            xGoogleAnalytics["includeProductCategory"],
                        ),
                    })),
                },
            };

            dataLayer.push({ ecommerce: null });
            dataLayer.push(googleAnalyticsData);
        }
    }, [isPLP, PLPProducts?.length, PLPInfoLoaded, xGoogleAnalytics?.productImpressionEvent]);

    // PDP View Event
    useEffect(() => {
        const dataLayer = getDataLayer();
        if (
            dataLayer &&
            isPDP &&
            xGoogleAnalytics?.pdpViewEvent &&
            xGoogleAnalytics?.selectItemEvent &&
            PDPProduct &&
            PDPProduct.pricing &&
            lastViewedPDP.current !== PDPProduct.productNumber
        ) {
            lastViewedPDP.current = PDPProduct.productNumber;

            const item = hasProductInViewItemList(PDPProduct.productNumber);
            if (item && xGoogleAnalytics?.selectItemEvent) {
                setPLPInfoLoaded(false);

                const googleAnalyticsData = {
                    event: xGoogleAnalytics.selectItemEventName,
                    ecommerce: {
                        item_list_id: xGoogleAnalytics["includeProductCategory"]
                            ? PDPProduct.properties?.["categoryId"] || ""
                            : undefined,
                        item_list_name: "Product Details Page",
                        affiliation,
                        bill_to_num: billToNum,
                        ship_to_num: shipToNum,
                        is_rep: isRep,
                        cust_type: customerType,
                        login_type: loginType,
                        warehouse,
                        sales_rep: salesRep,
                        items: [
                            {
                                item_name: PDPProduct?.productTitle,
                                item_id: PDPProduct?.productNumber,
                                price: PDPProduct.pricing?.unitNetPrice,
                                discount: PDPProduct.pricing
                                    ? PDPProduct.pricing.unitNetPrice < PDPProduct.pricing.unitListPrice
                                        ? PDPProduct.pricing.unitListPrice - PDPProduct.pricing.unitNetPrice
                                        : 0
                                    : 0,
                                index: 0,
                                item_brand: xGoogleAnalytics["includeProductBrand"]
                                    ? PDPProduct.brand?.name || ""
                                    : undefined,
                                ...parseProductCategories(
                                    PDPProduct?.properties?.itemCategoriesShortDescription,
                                    true,
                                    xGoogleAnalytics["includeProductCategory"],
                                ),
                                item_list_id: item.item_list_id,
                                item_list_name: item.item_list_name,
                                affiliation,
                                location_id: locationId,
                                qunatity: PDPProduct.minimumOrderQty || 1,
                            },
                        ],
                    },
                };

                dataLayer.push({ ecommerce: null });
                dataLayer.push(googleAnalyticsData);
            }

            const googleAnalyticsData = {
                event: xGoogleAnalytics.pdpViewEventName,
                ecommerce: {
                    currency: currencyCode,
                    value: PDPProduct?.quoteRequired ? "Requires Quote" : PDPProduct?.pricing?.unitNetPrice || 0,
                    url: PDPProduct?.canonicalUrl,
                    affiliation,
                    bill_to_num: billToNum,
                    ship_to_num: shipToNum,
                    is_rep: isRep,
                    cust_type: customerType,
                    login_type: loginType,
                    warehouse,
                    sales_rep: salesRep,
                    items: [
                        {
                            item_name: PDPProduct?.productTitle,
                            item_id: PDPProduct?.productNumber,
                            item_variant: "",
                            items_available: PDPProduct.inventory?.qtyOnHand || 0,
                            price: PDPProduct.pricing?.unitNetPrice || 0,
                            discount: PDPProduct.pricing
                                ? PDPProduct.pricing.unitNetPrice < PDPProduct.pricing.unitListPrice
                                    ? PDPProduct.pricing.unitListPrice - PDPProduct.pricing.unitNetPrice
                                    : 0
                                : 0,
                            index: 0,
                            item_list_name: "Product Detail Page",
                            item_brand: xGoogleAnalytics["includeProductBrand"]
                                ? PDPProduct.brand?.name || ""
                                : undefined,
                            item_list_id: xGoogleAnalytics["includeProductCategory"]
                                ? PDPProduct.properties?.["categoryId"] || ""
                                : undefined,
                            affiliation,
                            quantity: PDPProduct.minimumOrderQty || 1,
                            default_state: "No UOM selected",
                            location_id: locationId,
                            ...parseProductCategories(
                                PDPProduct?.properties?.itemCategoriesShortDescription,
                                true,
                                xGoogleAnalytics["includeProductCategory"],
                            ),
                        },
                    ],
                },
            };

            const isDuplicatedEvent =
                dataLayer[dataLayer.length - 1]?.event === xGoogleAnalytics?.pdpViewEventName &&
                dataLayer[dataLayer.length - 1]?.ecommerce.items[0].item_id === PDPProduct?.productNumber;

            if (!isDuplicatedEvent) {
                dataLayer.push({ ecommerce: null });
                dataLayer.push(googleAnalyticsData);
            }
        }
        if (!isPDP) {
            lastViewedPDP.current = "";
        }
    }, [
        currentPage,
        PDPProduct?.productNumber,
        PDPProduct?.pricing,
        xGoogleAnalytics?.pdpViewEvent,
        xGoogleAnalytics?.selectItemEvent,
    ]);

    useEffect(() => {
        const dataLayer = getDataLayer();

        if (dataLayer && xGoogleAnalytics?.shouldTrackPayment) {
            (window as any)._GaCheckoutFunnel(
                3,
                cart,
                currencyCode,
                affiliation,
                xGoogleAnalytics.includeProductBrand,
                xGoogleAnalytics.includeProductCategory,
                "payment",
            );
            resetShouldTrackPayment();
        }
    }, [xGoogleAnalytics?.shouldTrackPayment]);

    useEffect(() => {
        const dataLayer = getDataLayer();
        if (dataLayer && xGoogleAnalytics?.checkoutFlowEvents && cart && xGoogleAnalytics?.shouldTrackShippingInfo) {
            (window as any)._GaCheckoutFunnel(
                2,
                cart,
                currencyCode,
                affiliation,
                xGoogleAnalytics.includeProductBrand,
                xGoogleAnalytics.includeProductCategory,
                "shipping",
            );
        }
        resetShouldTrackShippingInfo();
    }, [xGoogleAnalytics?.shouldTrackShippingInfo, cart]);

    useEffect(() => {
        setAfilliation(affiliation);
    }, [affiliation]);

    useEffect(() => {
        setCustomerNumbers(billToNum, shipToNum);
    }, [billToNum, shipToNum]);

    useEffect(() => {
        setSalesRep(salesRep);
    }, [salesRep]);

    useEffect(() => {
        setIsRep(isRep);
    }, [isRep]);

    useEffect(() => {
        setCustomerType(customerType);
    }, [customerType]);

    useEffect(() => {
        setLoginType(loginType);
    }, [loginType]);

    useEffect(() => {
        setWarehouse(warehouse);
    }, [warehouse]);

    // Function to check for view_item_list events with a specific productNumber
    const hasProductInViewItemList = (productNumber: string) => {
        const dataLayer = getDataLayer();
        if (dataLayer) {
            for (const event of dataLayer) {
                if (event.event === xGoogleAnalytics?.productImpressionEventName && event.ecommerce?.items) {
                    for (const item of event.ecommerce.items) {
                        if (item.item_id === productNumber) {
                            return item;
                        }
                    }
                }
            }
        }
        return false;
    };

    return null;
};

const basicEventsTab = {
    displayName: "Basic Events",
    sortOrder: 1,
};
const customEventsTab = {
    displayName: "Custom Events",
    sortOrder: 2,
};
const settingsTab = {
    displayName: "Settings",
    sortOrder: 3,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(GoogleAnalyticsConnector),
    definition: {
        displayName: "Google Analytics Connector",
        group: XNGAGE_CUSTOM_WIDGET,
        fieldDefinitions: [
            {
                name: fields.trackGoogleAnalyticsEvents,
                displayName: "Track GoogleAnalytics Events",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
            },
            {
                name: fields.addToCartEvent,
                displayName: "Add To Cart (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.addToCartEventName,
                displayName: "Add To Cart (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "add_to_cart",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.addToCartEvent,
            },
            {
                name: fields.removeFromCartLineEvent,
                displayName: "Remove Cartline (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.removeFromCartLineEventName,
                displayName: "Remove Cartline (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "remove_from_cart",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.removeFromCartLineEvent,
            },
            {
                name: fields.addToWishListEvent,
                displayName: "Add to wishlist (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.addToWishListEventName,
                displayName: "Add to wishlist (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "add_to_wishlist",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.addToWishListEvent,
            },
            {
                name: fields.pdpViewEvent,
                displayName: "PDP View (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.pdpViewEventName,
                displayName: "PDP View (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "view_item",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.pdpViewEvent,
            },
            {
                name: fields.productImpressionEvent,
                displayName: "Product Impression (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.productImpressionEventName,
                displayName: "Product Impression (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "view_item_list",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.productImpressionEvent,
            },
            {
                name: fields.selectItemEvent,
                displayName: "Select Item (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.selectItemEventName,
                displayName: "Select Item (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "select_item",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.selectItemEvent,
            },
            {
                name: fields.viewCartEvent,
                displayName: "View Cart (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.viewCartEventName,
                displayName: "View Cart (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "view_cart",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.viewCartEvent,
            },
            {
                name: fields.purchaseEvent,
                displayName: "Purchase (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.purchaseEventName,
                displayName: "Purchase (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "purchase",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.purchaseEvent,
            },
            {
                name: fields.checkoutFlowEvents,
                displayName: "Checkout Flow (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.loginEvent,
                displayName: "Login Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.loginEventName,
                displayName: "Login Event Name",
                editorTemplate: "TextField",
                defaultValue: "login",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.loginEvent,
            },
            {
                name: fields.searchEvent,
                displayName: "Search Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.searchEventName,
                displayName: "Search Event Name",
                editorTemplate: "TextField",
                defaultValue: "search",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.searchEvent,
            },
            {
                name: fields.contactUsEvent,
                displayName: "Contact Us Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.contactUsEventName,
                displayName: "Contact Us Event Name",
                editorTemplate: "TextField",
                defaultValue: "contact_us",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.contactUsEvent,
            },
            {
                name: fields.plpFilterEvent,
                displayName: "PLP Filter Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.plpFilterEventName,
                displayName: "PLP Filter Event Name",
                editorTemplate: "TextField",
                defaultValue: "plp_filter",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.plpFilterEvent,
            },
            {
                name: fields.plpSearchEvent,
                displayName: "PLP Search Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.plpSearchEventName,
                displayName: "PLP Search Event Name",
                editorTemplate: "TextField",
                defaultValue: "plp_search",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.plpSearchEvent,
            },
            {
                name: fields.pdpShareEvent,
                displayName: "PDP Share Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.pdpShareEventName,
                displayName: "PDP Share Event Name",
                editorTemplate: "TextField",
                defaultValue: "pdp_share",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.pdpShareEvent,
            },
            {
                name: fields.requestAQuoteEvent,
                displayName: "Request A Quote Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.requestAQuoteEventName,
                displayName: "Request A Quote Event Name",
                editorTemplate: "TextField",
                defaultValue: "request_a_quote",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.requestAQuoteEvent,
            },
            {
                name: fields.myAcctQuickLinksEvent,
                displayName: "My Account Quick Links Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.myAcctQuickLinksEventName,
                displayName: "My Account Quick Links Event Name",
                editorTemplate: "TextField",
                defaultValue: "my_acct_quicklinks",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.myAcctQuickLinksEvent,
            },
            {
                name: fields.myAcctFlyoutEvent,
                displayName: "My Account Flyout Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.myAcctFlyoutEventName,
                displayName: "My Account Flyout Event Name",
                editorTemplate: "TextField",
                defaultValue: "my_acct_flyout",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.myAcctFlyoutEvent,
            },
            {
                name: fields.navBarClickEvent,
                displayName: "NavBar Click Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.navBarClickEventName,
                displayName: "NavBar Click Event Name",
                editorTemplate: "TextField",
                defaultValue: "nav_bar_click",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.navBarClickEvent,
            },
            {
                name: fields.toolBarNavEvent,
                displayName: "Tool Bar Navigation Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.toolBarNavEventName,
                displayName: "Tool Bar Navigation Event Name",
                editorTemplate: "TextField",
                defaultValue: "tool_bar_nav",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.toolBarNavEvent,
            },
            {
                name: fields.requestAccountEvent,
                displayName: "Request Account Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.requestAccountEventName,
                displayName: "Request Account Event Name",
                editorTemplate: "TextField",
                defaultValue: "request_account",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.requestAccountEvent,
            },
            {
                name: fields.locationsEvent,
                displayName: "Locations Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.locationsEventName,
                displayName: "Locations Event Name",
                editorTemplate: "TextField",
                defaultValue: "locations",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.locationsEvent,
            },
            {
                name: fields.myacctMylistsEvent,
                displayName: "My Account My Lists Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.myacctMylistsEventName,
                displayName: "My Account My Lists Event Name",
                editorTemplate: "TextField",
                defaultValue: "myacct_mylists",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.myacctMylistsEvent,
            },
            {
                name: fields.myacctQuotesEvent,
                displayName: "My Account Quotes Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.myacctQuotesEventName,
                displayName: "My Account Quotes Event Name",
                editorTemplate: "TextField",
                defaultValue: "myacct_quotes",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.myacctQuotesEvent,
            },
            {
                name: fields.addListToCartEvent,
                displayName: "Add List to Cart Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.addListToCartEventName,
                displayName: "Add List to Cart Event Name",
                editorTemplate: "TextField",
                defaultValue: "add_list_to_cart",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.addListToCartEvent,
            },
            {
                name: fields.myacctListsMoreactionsEvent,
                displayName: "My Account Lists More Actions Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.myacctListsMoreactionsEventName,
                displayName: "My Account Lists More Actions Event Name",
                editorTemplate: "TextField",
                defaultValue: "myacct_lists_moreactions",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.myacctListsMoreactionsEvent,
            },
            {
                name: fields.quickOrderSearchEvent,
                displayName: "Quick Order Search Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.quickOrderSearchEventName,
                displayName: "Quick Order Search Event Name",
                editorTemplate: "TextField",
                defaultValue: "quick_order_search",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.quickOrderSearchEvent,
            },
            {
                name: fields.quickOrderUploadEvent,
                displayName: "Quick Order Upload Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.quickOrderUploadEventName,
                displayName: "Quick Order Upload Event Name",
                editorTemplate: "TextField",
                defaultValue: "quick_order_upload",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.quickOrderUploadEvent,
            },
            {
                name: fields.requestProductEvent,
                displayName: "Request Product Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.requestProductEventName,
                displayName: "Request Product Event Name",
                editorTemplate: "TextField",
                defaultValue: "request_product",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.requestProductEvent,
            },
            {
                name: fields.editCartEvent,
                displayName: "Edit Cart Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.editCartEventName,
                displayName: "Edit Cart Event Name",
                editorTemplate: "TextField",
                defaultValue: "edit_cart",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.editCartEvent,
            },
            {
                name: fields.reorderEvent,
                displayName: "Reorder Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.reorderEventName,
                displayName: "Reorder Event Name",
                editorTemplate: "TextField",
                defaultValue: "reorder",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.reorderEvent,
            },
            {
                name: fields.myacctOrderhistoryDaterangeEvent,
                displayName: "Order History Date Range Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.myacctOrderhistoryDaterangeEventName,
                displayName: "Order History Date Range Event Name",
                editorTemplate: "TextField",
                defaultValue: "myacct_orderhistory_daterange",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.myacctOrderhistoryDaterangeEvent,
            },
            {
                name: fields.myacctOrderhistoryShiptoEvent,
                displayName: "Order History Ship To Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.myacctOrderhistoryShiptoEventName,
                displayName: "Order History Ship To Event Name",
                editorTemplate: "TextField",
                defaultValue: "myacct_orderhistory_shipto",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.myacctOrderhistoryShiptoEvent,
            },
            {
                name: fields.myacctOrderEvent,
                displayName: "My Account Order Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.myacctOrderEventName,
                displayName: "My Account Order Event Name",
                editorTemplate: "TextField",
                defaultValue: "myacct_order",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.myacctOrderEvent,
            },
            {
                name: fields.quoteAcceptedEvent,
                displayName: "Quote Accepted Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.quoteAcceptedEventName,
                displayName: "Quote Accepted Event Name",
                editorTemplate: "TextField",
                defaultValue: "quote_accepted",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.quoteAcceptedEvent,
            },
            {
                name: fields.quoteDeclinedEvent,
                displayName: "Quote Declined Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.quoteDeclinedEventName,
                displayName: "Quote Declined Event Name",
                editorTemplate: "TextField",
                defaultValue: "quote_declined",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.quoteDeclinedEvent,
            },
            {
                name: fields.quoteProposedEvent,
                displayName: "Quote Proposed Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.quoteProposedEventName,
                displayName: "Quote Proposed Event Name",
                editorTemplate: "TextField",
                defaultValue: "quote_proposed",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.quoteProposedEvent,
            },
            {
                name: fields.contactSalesEvent,
                displayName: "Contact Sales Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.contactSalesEventName,
                displayName: "Contact Sales Event Name",
                editorTemplate: "TextField",
                defaultValue: "contact_sales",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.contactSalesEvent,
            },
            {
                name: fields.pdpImageClickEvent,
                displayName: "PDP Image Click Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.pdpImageClickEventName,
                displayName: "PDP Image Click Event Name",
                editorTemplate: "TextField",
                defaultValue: "pdp_image_click",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.pdpImageClickEvent,
            },
            {
                name: fields.myacctAddUserEvent,
                displayName: "My Account Add User Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.myacctAddUserEventName,
                displayName: "My Account Add User Event Name",
                editorTemplate: "TextField",
                defaultValue: "myacct_add_user",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.myacctAddUserEvent,
            },
            {
                name: fields.myacctEditUserEvent,
                displayName: "My Account Edit User Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.myacctEditUserEventName,
                displayName: "My Account Edit User Event Name",
                editorTemplate: "TextField",
                defaultValue: "myacct_edit_user",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.myacctEditUserEvent,
            },
            {
                name: fields.myacctViewUserEvent,
                displayName: "My Account View User Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.myacctViewUserEventName,
                displayName: "My Account View User Event Name",
                editorTemplate: "TextField",
                defaultValue: "myacct_view_user",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.myacctViewUserEvent,
            },
            {
                name: fields.footerClicksEvent,
                displayName: "Footer Clicks Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.footerClicksEventName,
                displayName: "Footer Clicks Event Name",
                editorTemplate: "TextField",
                defaultValue: "footer_clicks",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.footerClicksEvent,
            },
            {
                name: fields.fileDownloadEvent,
                displayName: "File Download Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.fileDownloadEventName,
                displayName: "File Download Event Name",
                editorTemplate: "TextField",
                defaultValue: "file_download",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.fileDownloadEvent,
            },
            {
                name: fields.pdpWarehouseInventoryEvent,
                displayName: "PDP Warehouse Inventory Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.pdpWarehouseInventoryEventName,
                displayName: "PDP Warehouse Inventory Event Name",
                editorTemplate: "TextField",
                defaultValue: "pdp_warehouse_inventory",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.pdpWarehouseInventoryEvent,
            },
            {
                name: fields.loginIssueEvent,
                displayName: "Login Issue Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.loginIssueEventName,
                displayName: "Login Issue Event Name",
                editorTemplate: "TextField",
                defaultValue: "login_issue",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.loginIssueEvent,
            },
            {
                name: fields.industriesServedEvent,
                displayName: "Industries Served Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.industriesServedEventName,
                displayName: "Industries Served Event Name",
                editorTemplate: "TextField",
                defaultValue: "industries_served",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.industriesServedEvent,
            },
            {
                name: fields.myacctRecentOrdersEvent,
                displayName: "My Account Recent Orders Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.myacctRecentOrdersEventName,
                displayName: "My Account Recent Orders Event Name",
                editorTemplate: "TextField",
                defaultValue: "myacct_recent_orders",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.myacctRecentOrdersEvent,
            },
            {
                name: fields.homepageRibbonevent,
                displayName: "Homepage Ribbon Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.homepageRibbonEventName,
                displayName: "Homepage Ribbon Event Name",
                editorTemplate: "TextField",
                defaultValue: "homepage_ribbon",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.homepageRibbonevent,
            },
            {
                name: fields.includeProductBrand,
                displayName: "Include Product Brand (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeProductCategory,
                displayName: "Include Product Category (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
        ],
    },
};

export default widgetModule;
